import { useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  GuideAnnotationIntentSchema,
  type GuideAnnotation,
  type GuideAnnotationIntent,
} from '@erp/rpc';
import { Annotator, type Stroke } from 'src/components/common/Annotator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  toAnnotatorStrokes,
  toGuideAnnotations,
  type StageRect,
} from '../guideAnnotations';

/**
 * Draw on a guide frame (AI-488 follow-on). The Annotator works in stage pixels over the rendered
 * image; on save the strokes are normalized to the image and handed back as `GuideAnnotation`s.
 * The server renders the canonical flattened image, so the client-side export is not used.
 */
export function SopGuideAnnotateDialog({
  open,
  onOpenChange,
  imageUrl,
  title,
  annotations,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  title: string;
  annotations: GuideAnnotation[];
  onSave: (annotations: GuideAnnotation[]) => void;
}) {
  const gt = useGT();
  const [intent, setIntent] = useState<GuideAnnotationIntent>('act_here');
  const [rect, setRect] = useState<StageRect | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const frameRef = useRef<HTMLDivElement>(null);

  // Fit the picture to the screen: as wide as the dialog allows and no taller than the viewport
  // leaves after the header, toolbar and footer, keeping the aspect ratio. The image is sized
  // explicitly to that box so the element box IS the painted picture (no letterboxing), and the
  // drawing stage is wrapped to the same width so stroke coordinates map onto it 1:1.
  const measure = () => {
    const img = imgRef.current;
    const frame = frameRef.current;
    if (!img || !frame || !img.naturalWidth || !img.naturalHeight) return;
    const maxWidth = frame.clientWidth;
    const maxHeight = Math.max(240, window.innerHeight * 0.94 - 300);
    const scale = Math.min(
      maxWidth / img.naturalWidth,
      maxHeight / img.naturalHeight,
    );
    setRect({
      width: Math.floor(img.naturalWidth * scale),
      height: Math.floor(img.naturalHeight * scale),
    });
  };
  useEffect(() => {
    if (!open) return;
    const frame = frameRef.current;
    if (!frame) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(frame);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  const intentLabel: Record<GuideAnnotationIntent, string> = {
    act_here: gt('Where to act'),
    read: gt('What to check'),
    avoid: gt('What to avoid'),
    note: gt('Just a note'),
  };

  const save = (strokes: Stroke[]) => {
    if (!rect) return;
    onSave(toGuideAnnotations(strokes, rect, intent, annotations));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[94vh] w-[min(96vw,1800px)] max-w-[min(96vw,1800px)] overflow-y-auto sm:max-w-[min(96vw,1800px)]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            <T context="help text in the guide frame annotation dialog">
              Circle or point at the control the author used. The agent sees
              your marks as guidance about this screen, never as values to type.
            </T>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Label className="text-xs">
            <T context="label for the guide annotation intent picker">
              Marks mean
            </T>
          </Label>
          <Select
            value={intent}
            onValueChange={(value) => {
              const parsed = GuideAnnotationIntentSchema.safeParse(value);
              if (parsed.success) setIntent(parsed.data);
            }}
          >
            <SelectTrigger className="h-7 w-auto text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GuideAnnotationIntentSchema.options.map((value) => (
                <SelectItem key={value} value={value}>
                  {intentLabel[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* `frameRef` measures the available width; the inner wrapper shrinks to the fitted image so
            the Annotator's stage (and its stroke coordinates) coincide with the picture. The
            Annotator re-seeds from the stored marks whenever the fitted size changes. */}
        <div ref={frameRef} className="w-full">
          <div
            className="mx-auto"
            style={rect ? { width: rect.width } : undefined}
          >
            <Annotator
              key={rect ? `${rect.width}x${rect.height}` : 'unsized'}
              saveLabel={gt('Save marks')}
              initialStrokes={rect ? toAnnotatorStrokes(annotations, rect) : []}
              onSave={save}
            >
              <img
                ref={imgRef}
                src={imageUrl}
                alt={title}
                onLoad={measure}
                className="block"
                style={
                  rect
                    ? { width: rect.width, height: rect.height }
                    : { width: '100%', height: 'auto' }
                }
                draggable={false}
              />
            </Annotator>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
